import { useState, useRef, useEffect } from 'react';
import { api } from '@/shared/lib/api';

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function renderMarkdown(text: string): string {
  let html = escapeHtml(text)
    // Блоки кода ```...```
    .replace(/```\n?([\s\S]*?)\n?```/g, '<pre style="background:rgba(0,0,0,0.05);padding:8px;border-radius:6px;overflow-x:auto;font-size:12px;"><code>$1</code></pre>')
    // Жирный: **text** или __text__
    .replace(/\*\*(.+?)\*\*/g, '<strong style="font-weight:600;">$1</strong>')
    .replace(/__(.+?)__/g, '<strong style="font-weight:600;">$1</strong>')
    // Курсив: *text* (только если не маркер списка — между * и * без переноса строки)
    .replace(/\*([^\n*]+?)\*/g, '<em>$1</em>')
    .replace(/_([^\n_]+?)_/g, '<em>$1</em>')
    // Строчный код `code`
    .replace(/`([^`]+)`/g, '<code style="background:rgba(0,0,0,0.05);padding:2px 4px;border-radius:4px;font-size:12px;">$1</code>');

  // Маркеры списка: строки начинающиеся с "* " или "- " → превращаем в <ul><li>
  const lines = html.split('\n');
  let inList = false;
  let result = '';
  for (const line of lines) {
    const trimmed = line.trimStart();
    if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
      if (!inList) {
        result += '<ul style="margin:4px 0;padding-left:20px;">';
        inList = true;
      }
      result += `<li style="margin:2px 0;">${trimmed.slice(2)}</li>`;
    } else {
      if (inList) {
        result += '</ul>';
        inList = false;
      }
      result += line + '\n';
    }
  }
  if (inList) result += '</ul>';

  return result;
}

interface Message {
  id: number;
  role: 'user' | 'bot';
  text: string;
}

interface AiChatBotProps {
  onClose: () => void;
  context?: string;
  initialMessage?: string;
  inline?: boolean;
}

export function AiChatBot({ onClose, context, initialMessage, inline }: AiChatBotProps) {
  const msgIdRef = useRef(0);
  const nextId = () => ++msgIdRef.current;

  const [messages, setMessages] = useState<Message[]>([
    { id: nextId(), role: 'bot', text: initialMessage || 'Привет! Я 2EnBota 🦊🦝 — твой AI-помощник для изучения английского. Спроси меня что угодно!' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || isLoading) return;

    setInput('');
    setMessages(prev => [...prev, { id: nextId(), role: 'user', text }]);
    setIsLoading(true);

    try {
      // Передаём историю диалога (кроме текущего сообщения, которое уже добавили)
      const historyForApi = messages.map(m => ({ role: m.role, text: m.text }));
      const result = await api.post<{ reply: string }>('/ai/chat', {
        message: text,
        context,
        history: historyForApi,
      });
      setMessages(prev => [...prev, { id: nextId(), role: 'bot', text: result.reply }]);
    } catch {
      setMessages(prev => [...prev, { id: nextId(), role: 'bot', text: 'Извини, что-то пошло не так. Попробуй ещё раз! 🦊' }]);
    } finally {
      setIsLoading(false);
    }
  };

  const containerStyle = inline
    ? {
        background: 'var(--theme-bg)',
        display: 'flex', flexDirection: 'column' as const,
        borderRadius: 16,
        border: '1px solid var(--theme-border)',
        overflow: 'hidden',
        marginTop: 12,
      }
    : {
        position: 'fixed' as const, inset: 0, zIndex: 200,
        background: 'var(--theme-bg)',
        display: 'flex', flexDirection: 'column' as const,
      };

  return (
    <div style={containerStyle} data-chat-container="true">
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px',
        background: 'var(--theme-bg-elevated)',
        borderBottom: '1px solid var(--theme-border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 28 }}>🦊🦝</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--theme-text)' }}>2EnBota AI</div>
            <div style={{ fontSize: 11, color: 'var(--theme-success)' }}>● Онлайн</div>
          </div>
        </div>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--theme-text-secondary)', cursor: 'pointer', padding: 4 }}
        >
          ✕
        </button>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
        {messages.map((msg) => (
          <div
            key={msg.id}
            style={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: 10,
            }}
          >
            <div style={{
              maxWidth: '80%',
              padding: '10px 14px',
              borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
              background: msg.role === 'user' ? 'var(--theme-primary)' : 'var(--theme-bg-elevated)',
              color: msg.role === 'user' ? '#111' : 'var(--theme-text)',
              border: msg.role === 'bot' ? '1px solid var(--theme-border)' : 'none',
              fontSize: 14,
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
            }}
              dangerouslySetInnerHTML={{ __html: msg.role === 'bot' ? renderMarkdown(msg.text) : escapeHtml(msg.text) }}
            />
          </div>
        ))}

        {isLoading && (
          <div style={{ display: 'flex', gap: 4, padding: '8px 0' }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: 8, height: 8, borderRadius: '50%',
                background: 'var(--theme-text-muted)',
                animation: `bounce 1s ${i * 0.15}s infinite`,
              }} />
            ))}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{
        display: 'flex',
        gap: 8,
        padding: '12px 16px',
        borderTop: '1px solid var(--theme-border)',
        background: 'var(--theme-bg-elevated)',
        paddingBottom: 'calc(12px + var(--safe-area-bottom))',
      }}>
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
          placeholder="Спроси что-нибудь..."
          style={{
            flex: 1,
            padding: '10px 14px',
            background: 'var(--theme-bg)',
            border: '1px solid var(--theme-border)',
            borderRadius: 20,
            color: 'var(--theme-text)',
            fontSize: 14,
            outline: 'none',
          }}
        />
        <button
          onClick={sendMessage}
          disabled={!input.trim() || isLoading}
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: input.trim() ? 'var(--theme-primary)' : 'var(--theme-border)',
            border: 'none',
            color: 'var(--theme-primary-text)',
            fontSize: 20,
            cursor: input.trim() ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          ↑
        </button>
      </div>

    </div>
  );
}
